import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Card, Checklist, CopyButton, ErrorBox, Page, Spinner, Stat, StatusBadge, useAsync, Empty
} from '../../components/Ui.jsx'
import { describeEvent, useLive, useLiveEvent } from '../../lib/live.jsx'
import { fmtStamp, inr, lakh } from '../../lib/format.js'
import { publicDirectoryUrl } from '../../lib/share.js'

const SETUP_KEY = 'aqua.setup.dismissed'

export default function Overview () {
  const { data, error, loading, reload } = useAsync(
    () => api.get('/admin/overview'), [],
    { live: ['orders', 'batches', 'hatcheries', 'enquiries', 'farmers', 'network', 'users'] }
  )
  const { presence } = useLive()
  const [flash, setFlash] = useState(null)
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(SETUP_KEY) === '1' } catch { return false }
  })

  // A one-line note of what just moved, so the number changing on screen has a
  // cause the admin can see.
  useLiveEvent(['orders', 'batches', 'enquiries'], payload => {
    setFlash(payload)
    setTimeout(() => setFlash(f => (f === payload ? null : f)), 6000)
  })

  useEffect(() => {
    if (dismissed) { try { localStorage.setItem(SETUP_KEY, '1') } catch { /* private mode */ } }
  }, [dismissed])

  if (loading) return <Page title="Overview"><Spinner /></Page>
  if (error) return <Page title="Overview"><ErrorBox error={error} onRetry={reload} /></Page>

  const { counts, recent, hatcheries } = data
  const directory = publicDirectoryUrl()

  const setup = [
    {
      title: 'Onboard your first hatchery',
      body: 'Create the hatchery record and issue its portal login in one guided pass.',
      done: counts.hatcheries > 0,
      action: <Link className="btn sm primary" to="/admin/hatcheries/new">Onboard hatchery</Link>
    },
    {
      title: 'Get a batch listed',
      body: 'A hatchery posts its ready batch — nothing can be matched or shown to a farmer until one is live.',
      done: counts.liveBatches > 0,
      action: <Link className="btn sm" to="/admin/hatcheries">Open a hatchery</Link>
    },
    {
      title: 'Put an operator on the desk',
      body: 'Operators take farmer calls, match batches and book. Admin can stand in, but the desk needs its own login.',
      done: counts.operators > 0,
      action: <Link className="btn sm" to="/admin/users">Add a login</Link>
    },
    {
      title: 'Set up transport',
      body: 'At least one pickup vehicle and one liaison officer, or a booking cannot be dispatched.',
      done: counts.vehicles > 0 && counts.liaisons > 0,
      action: <Link className="btn sm" to="/admin/network">Transport network</Link>
    },
    {
      title: 'Send the farmer link',
      body: 'Share the public directory over WhatsApp. Farmers browse and request seed; the desk books it.',
      done: counts.farmers > 0 || counts.newEnquiries > 0,
      action: <CopyButton className="btn sm" value={directory} told="Farmer link copied">Copy farmer link</CopyButton>
    },
    {
      title: 'Book the first order',
      body: 'Match a farmer to a batch — the sample test and logistics follow from there.',
      done: counts.openOrders > 0 || counts.seedLakh > 0,
      action: <Link className="btn sm" to="/operator/match">Match & book</Link>
    }
  ]
  const remaining = setup.filter(s => !s.done).length
  const showSetup = remaining > 0 && !dismissed

  return (
    <Page
      title="Platform overview"
      subtitle="Everything the desk is carrying right now — hatcheries onboarded, seed booked, and the exceptions that need a decision."
      actions={
        <>
          <Link className="btn" to="/admin/share">Farmer link</Link>
          <Link className="btn primary" to="/admin/hatcheries/new">Onboard hatchery</Link>
        </>
      }
    >
      {flash && (
        <div className="livebar">
          <span className="dot" />
          <b>{describeEvent(flash)}</b> · {flash.by}
          <span className="muted">just now — the numbers below already include it</span>
        </div>
      )}

      {showSetup && (
        <Card
          title={`Set up the platform · ${setup.length - remaining} of ${setup.length} done`}
          className="setup"
          actions={<button className="btn sm ghost" onClick={() => setDismissed(true)}>Hide</button>}
        >
          <Checklist items={setup} />
        </Card>
      )}

      <div className="grid four" style={{ marginBottom: 18 }}>
        <Stat value={counts.hatcheries} label="Active hatcheries" sub={`${counts.liveBatches} batches open for booking`} />
        <Stat value={counts.openOrders} label="Open bookings" sub={`${lakh(counts.seedLakh)} PL booked to date`} />
        <Stat value={inr(counts.value)} label="Booking value" sub={`${inr(counts.commission)} platform commission`} />
        <Stat value={counts.farmers} label="Farmers on file" sub={`${inr(counts.paidOut)} paid out to hatcheries`} />
      </div>

      <div className="grid four" style={{ marginBottom: 18 }}>
        <Stat tone={counts.newEnquiries > 0 ? 'warn' : ''} value={counts.newEnquiries} label="New enquiries"
          sub="Farmers who used the public link and are waiting for a call" />
        <Stat value={counts.operators} label="Operators on the desk" sub={`${presence.count} signed in right now`} />
        <Stat value={counts.vehicles} label="Vehicles in the network" sub={`${counts.liaisons} liaison officers active`} />
        <Stat tone={counts.flagged > 0 ? 'alert' : ''} value={counts.flagged} label="Batches flagged on quality"
          sub={`${counts.failedSamples} failed samples awaiting re-match`} />
      </div>

      <div className="split">
        <Card title="Recently onboarded hatcheries" padded={false}
          actions={<Link className="btn sm" to="/admin/hatcheries">See all</Link>}>
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Hatchery</th><th>Place</th><th className="num">Live batches</th><th className="num">Open orders</th><th /></tr>
              </thead>
              <tbody>
                {hatcheries.map(h => (
                  <tr key={h._id}>
                    <td>
                      <Link className="rowlink" to={`/admin/hatcheries/${h._id}`}>{h.name}</Link>
                      <span className="t-sub mono">{h.code}</span>
                    </td>
                    <td>{[h.village, h.mandal].filter(Boolean).join(', ')}</td>
                    <td className="num">{h.liveBatches}</td>
                    <td className="num">{h.openOrders}</td>
                    <td className="right"><Link className="btn sm" to={`/admin/hatcheries/${h._id}`}>Open</Link></td>
                  </tr>
                ))}
                {!hatcheries.length && <tr><td colSpan={5}><Empty>No hatcheries onboarded yet.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Latest movement" padded={false}
          actions={<Link className="btn sm" to="/operator/orders">All bookings</Link>}>
          <div className="scroll-x">
            <table>
              <tbody>
                {recent.map(o => (
                  <tr key={o._id}>
                    <td>
                      <Link className="rowlink mono" to={`/operator/orders/${o._id}`}>{o.orderCode}</Link>
                      <span className="t-sub">{o.farmer?.name} · {o.hatchery?.name}</span>
                    </td>
                    <td className="right">
                      <StatusBadge status={o.status} />
                      <span className="t-sub">{fmtStamp(o.updatedAt)}</span>
                    </td>
                  </tr>
                ))}
                {!recent.length && <tr><td><Empty>No bookings yet.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Page>
  )
}
